import React, { useState } from 'react';

import { Button } from '@components/Button';
import { ErrorMessage } from '@components/Forms/ErrorMessage';
import { FormAlert } from '@components/Forms/FormAlert';
import { instructionStrings } from '@data/instruction';
import { usePatchCustomsInstruction as usePatchDashboardCustomsInstruction } from '@hooks/mutation/dashboard/usePatchCustomsInstruction';
import { usePatchDeliveryInstruction as usePatchDashboardDeliveryInstruction } from '@hooks/mutation/dashboard/usePatchDeliveryInstruction';
import { CustomsInstruction } from '@models/CustomsInstruction';
import { DeliveryInstruction } from '@models/DeliveryInstruction';
import {
  type InstructionType,
  changeRequiredForm,
  changeRequiredFormInitialValues,
} from '@models/Instruction';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createValidate, formStrings } from '@utils/forms';
import { Form, Formik, Field } from 'formik';

const useStyles = makeMuiStyles((theme, common) => ({
  instructionChangeRequiredFormContent: {
    ...common.flexColumn,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    padding: 10,
  },
  instructionChangeRequiredFormTextarea: {
    height: 160,
    border: 'none',
    outline: 'none',
    resize: 'vertical',
  },
  instructionChangeRequiredFormActions: {
    ...common.flexInline,
    ...common.justifyEnd,
    marginTop: 10,
    columnGap: 10,
  },
}));

const InstructionChangeRequiredFormContent: React.FC<{
  type: InstructionType;
  onClose: () => void;
  d2dNo: string;
  onPatchDeliveryInstructionSuccess?: (v: DeliveryInstruction) => void;
  onPatchCustomsInstructionSuccess?: (v: CustomsInstruction) => void;
}> = ({
  type,
  onClose,
  d2dNo,
  onPatchDeliveryInstructionSuccess,
  onPatchCustomsInstructionSuccess,
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const patchDashboardCustomsInstruction = usePatchDashboardCustomsInstruction(d2dNo);
  const patchDashboardDeliveryInstruction = usePatchDashboardDeliveryInstruction(d2dNo);

  return (
    <Formik
      initialValues={changeRequiredFormInitialValues}
      validate={createValidate(changeRequiredForm)}
      onSubmit={async ({ message }) => {
        if (type === 'customsInstruction') {
          const customsInstruction = await patchDashboardCustomsInstruction.mutateAsync({
            status_name: '修正依頼済',
            message,
          });
          onPatchCustomsInstructionSuccess?.(customsInstruction);
        } else if (type === 'deliveryInstruction') {
          const deliveryInstruction = await patchDashboardDeliveryInstruction.mutateAsync({
            status_name: '修正依頼済',
            message,
          });
          onPatchDeliveryInstructionSuccess?.(deliveryInstruction);
        }
        onClose();
      }}
    >
      <Form className={classes.instructionChangeRequiredFormContent}>
        <FormAlert mutation={patchDashboardCustomsInstruction} gutter />
        <FormAlert mutation={patchDashboardDeliveryInstruction} gutter />
        <Field name="message">
          {({ field }: any) => (
            <>
              <textarea
                disabled={
                  patchDashboardCustomsInstruction.isLoading ||
                  patchDashboardDeliveryInstruction.isLoading
                }
                className={classes.instructionChangeRequiredFormTextarea}
                {...field}
              />
              <ErrorMessage name={field.name} />
            </>
          )}
        </Field>

        <div className={classes.instructionChangeRequiredFormActions}>
          <Button color="outlined" text={formStrings.cancel} size="smaller" onClick={onClose} />
          <Button
            color="primary"
            text={formStrings.submit}
            size="smaller"
            type="submit"
            isLoading={
              patchDashboardCustomsInstruction.isLoading ||
              patchDashboardDeliveryInstruction.isLoading
            }
          />
        </div>
      </Form>
    </Formik>
  );
};

export const InstructionChangeRequiredForm: React.FC<{
  type: InstructionType;
  title?: string;
  d2dNo: string;
  disabled: boolean;
  onPatchDeliveryInstructionSuccess?: (v: DeliveryInstruction) => void;
  onPatchCustomsInstructionSuccess?: (v: CustomsInstruction) => void;
}> = (props) => {
  const {
    type,
    d2dNo,
    disabled,
    onPatchDeliveryInstructionSuccess,
    onPatchCustomsInstructionSuccess,
    title,
  } = props;

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        color="primary"
        text={instructionStrings.requireChange}
        size="small"
        onClick={() => {
          setOpen(!open);
        }}
        disabled={disabled}
        title={title}
      />

      {open && (
        <InstructionChangeRequiredFormContent
          type={type}
          onClose={() => setOpen(false)}
          d2dNo={d2dNo}
          onPatchDeliveryInstructionSuccess={onPatchDeliveryInstructionSuccess}
          onPatchCustomsInstructionSuccess={onPatchCustomsInstructionSuccess}
        />
      )}
    </>
  );
};
